/**
 * Form state + helpers — A3 Branding (Sprint Frontend Brief 5 · Tela 4).
 *
 * Estado dos campos ESCALARES (cores/fonte/textos/certs) editados no formulário
 * e salvos num único PATCH. Assets (logo/favicon) ficam fora — upload imediato
 * por endpoint multipart dedicado.
 *
 * `buildPatch` faz diff contra o serializer carregado para enviar só o que mudou
 * (PATCH parcial `.strict()`): campo inalterado → omitido; string vazia em
 * texto/cor → `null` (zera no backend).
 */

import {
  HEX_COLOR_RE,
  MAX_CERTIFICATIONS,
  MAX_TEXT_LEN,
  type BrandingCertification,
  type SecondaryFont,
  type SerializedTenantBranding,
  type UpdateTenantBrandingRequest,
} from './types';

export interface BrandingFormState {
  primaryColor: string; // hex ou ''
  secondaryColor: string; // hex ou ''
  secondaryFont: SecondaryFont | '';
  emailSignature: string;
  regulatedHeaderText: string;
  certifications: BrandingCertification[];
}

export function brandingFormFromSerialized(b: SerializedTenantBranding): BrandingFormState {
  return {
    primaryColor: b.primaryColor ?? '',
    secondaryColor: b.secondaryColor ?? '',
    secondaryFont: (b.secondaryFont as SecondaryFont | null) ?? '',
    emailSignature: b.emailSignature ?? '',
    regulatedHeaderText: b.regulatedHeaderText ?? '',
    certifications: b.certifications,
  };
}

/** Normaliza '' → null (zera campo); mantém hex/enum/texto não-vazio. */
function emptyToNull(v: string): string | null {
  return v.trim() === '' ? null : v;
}

export function validateBrandingForm(form: BrandingFormState): string | null {
  if (form.primaryColor !== '' && !HEX_COLOR_RE.test(form.primaryColor)) {
    return 'Cor primária inválida (esperado hex #RRGGBB).';
  }
  if (form.secondaryColor !== '' && !HEX_COLOR_RE.test(form.secondaryColor)) {
    return 'Cor secundária inválida (esperado hex #RRGGBB).';
  }
  if (form.emailSignature.length > MAX_TEXT_LEN) {
    return `Assinatura de email excede ${String(MAX_TEXT_LEN)} caracteres.`;
  }
  if (form.regulatedHeaderText.length > MAX_TEXT_LEN) {
    return `Texto do header excede ${String(MAX_TEXT_LEN)} caracteres.`;
  }
  if (form.certifications.length > MAX_CERTIFICATIONS) {
    return `Máximo de ${String(MAX_CERTIFICATIONS)} certificações.`;
  }
  return null;
}

function certsEqual(a: BrandingCertification[], b: BrandingCertification[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((c, i) => c.id === b[i]?.id && c.label === b[i]?.label);
}

/**
 * Diff contra o serializer atual. Retorna só os campos alterados para o PATCH
 * parcial; `null` quando o usuário limpou um campo. Retorna `{}` quando nada mudou.
 */
export function buildBrandingPatch(
  form: BrandingFormState,
  current: SerializedTenantBranding,
): UpdateTenantBrandingRequest {
  const patch: UpdateTenantBrandingRequest = {};

  const primary = emptyToNull(form.primaryColor);
  if (primary !== (current.primaryColor ?? null)) patch.primaryColor = primary;

  const secondary = emptyToNull(form.secondaryColor);
  if (secondary !== (current.secondaryColor ?? null)) patch.secondaryColor = secondary;

  const font = form.secondaryFont === '' ? null : form.secondaryFont;
  if (font !== ((current.secondaryFont as SecondaryFont | null) ?? null))
    patch.secondaryFont = font;

  const signature = emptyToNull(form.emailSignature);
  if (signature !== (current.emailSignature ?? null)) patch.emailSignature = signature;

  const header = emptyToNull(form.regulatedHeaderText);
  if (header !== (current.regulatedHeaderText ?? null)) patch.regulatedHeaderText = header;

  if (!certsEqual(form.certifications, current.certifications)) {
    patch.certifications = form.certifications;
  }

  return patch;
}

export function isPatchEmpty(patch: UpdateTenantBrandingRequest): boolean {
  return Object.keys(patch).length === 0;
}

/** Valida um File de asset antes do upload (espelha as checagens do service). */
export function validateAssetFile(
  file: File,
  allowedMimes: string[],
  maxBytes: number,
): string | null {
  if (!allowedMimes.includes(file.type)) {
    const shown = file.type !== '' ? file.type : 'desconhecido';
    return `Tipo '${shown}' não aceito. Use: ${allowedMimes.join(', ')}.`;
  }
  if (file.size > maxBytes) {
    return `Arquivo excede ${String(Math.round(maxBytes / 1024))} KB.`;
  }
  return null;
}
